/* eslint-disable no-console */
import { useDispatch } from 'react-redux';
import { useFeatureFlag } from 'configcat-react';
import { Flex, FloraTypography } from '@grupoboticario/flora-react';
import { ChevronRightIcon } from '@grupoboticario/flora-react-icons';
import { paymentTypes } from './static-content';
import Styles from './PaymentsGroup.styles';
import { setPaymentOption, setStep, setView } from 'core/use-cases/checkout/actions/actions';
import type { PaymentInfo } from 'core/domains/cart';
import type { Checkout, PaymentMethods } from 'core/domains/checkout';
import { theme } from 'ui/theme';
import { BADGE_PIX_NAME, SHOW_PIX_BADGE } from 'core';
import { ResponsiveFloraTypography } from 'ui/components';

type PaymentGroupSelectProps = {
  setSelectedGroup(type: keyof PaymentMethods): void;
  paymentMethods: PaymentMethods;
};

export const PaymentGroupSelect = ({
  setSelectedGroup,
  paymentMethods,
}: PaymentGroupSelectProps) => {
  const dispatch = useDispatch();
  const paymentTypesKeys = Object.keys(paymentTypes) as Array<keyof PaymentMethods>;
  const { value: badgePixName } = useFeatureFlag(BADGE_PIX_NAME, 'Recomendado');
  const { value: showPixBadge, loading } = useFeatureFlag(SHOW_PIX_BADGE, false);
  const selectPaymentType = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.currentTarget;
    const selectedPaymentGroup = target.getAttribute('data-payment-type') as keyof PaymentMethods;
    const isSingleOption = target.getAttribute('data-single-payment') === 'true';
    if (isSingleOption) {
      const paymentOption = paymentMethods[selectedPaymentGroup];
      dispatch(
        setPaymentOption({
          code: (paymentOption?.paymentInfo[0] as PaymentInfo).paymentCode,
          mode: (paymentOption?.paymentInfo[0] as PaymentInfo).paymentMode,
          planType:
            paymentOption?.paymentPlanType === 'other'
              ? 'centralPayment'
              : paymentOption?.paymentPlanType,
        }),
      );
      dispatch(setStep('summary'));
      return;
    }
    if (selectedPaymentGroup === 'creditCard') {
      dispatch(setView('payment.creditCardForm'));
      return;
    }

    setSelectedGroup(selectedPaymentGroup);
  };
  return (
    <>
      {paymentTypesKeys?.map((paymentType) => {
        const paymentTypeData = paymentMethods[paymentType];
        const hasPaymentInfo = paymentTypeData?.paymentInfo?.length;

        if (
          !paymentMethods[paymentType] ||
          Object.keys(paymentTypeData as unknown as Checkout['paymentMethods']).length === 0
        ) {
          return;
        }
        const paymentStaticInfo = paymentTypes[paymentType];

        return (
          <Styles.PaymentOption
            key={paymentType}
            data-stargate
            data-border-emphansis={showPixBadge && paymentType === 'pixPayment'}
            data-testid={`payment-group-item-${paymentType}`}
            data-payment-type={paymentType}
            data-single-payment={paymentStaticInfo.singleOption}
            onClick={selectPaymentType}
            tabIndex={0}
            aria-label={`${paymentTypeData?.name} - ${paymentStaticInfo.label}`}
          >
            <Styles.PaymentOptionIcon>{paymentStaticInfo.icon}</Styles.PaymentOptionIcon>
            <Styles.PaymentOptionsLabels>
              <Styles.PaymentOptionTitle fontSize="bodySmallStandard">
                {paymentTypeData?.name}{' '}
              </Styles.PaymentOptionTitle>
              <Styles.PaymentOptionDescription fontSize="exceptionsAuxiliar">
                {paymentTypeData?.description}
              </Styles.PaymentOptionDescription>
            </Styles.PaymentOptionsLabels>
            <Flex css={{ justifyContent: 'flex-end' }}>
              {paymentType === 'pixPayment' && !loading && showPixBadge && (
                <Styles.PaymentOptionBadge data-stargate data-testid="payment-badge-pix">
                  <ResponsiveFloraTypography
                    fontSize="exceptionsAuxiliar"
                    fontWeight="regular"
                    color="$nonInteractiveAltPredominant"
                  >
                    {badgePixName}
                  </ResponsiveFloraTypography>
                </Styles.PaymentOptionBadge>
              )}
              <ChevronRightIcon size={16} />
            </Flex>
          </Styles.PaymentOption>
        );
      })}
    </>
  );
};
